import { formatDate } from "../../helpers/formatDate";

// Formats a numeric amount as a localized currency string, falling back to a
// plain "<amount> <CURRENCY>" if the currency code is one Intl can't resolve.
export const formatAmount = (amount, currency = "usd") => {
   const safeAmount = amount ?? 0;
   try {
      return new Intl.NumberFormat("en-US", {
         style: "currency",
         currency: currency.toUpperCase()
      }).format(safeAmount);
   } catch {
      return `${safeAmount} ${currency.toUpperCase()}`;
   }
};

// A failed funds call surfaces its reason either on the BaseResponse
// envelope's `message` or, when that's null, inside its `errors` array
// (e.g. ["Charge ch_... has already been refunded."]). The envelope arrives
// on the axios error response for HTTP 4xx, or as the rejection value itself
// for 200-with-isSuccess:false. Pull out the exact text so the user sees the
// actionable reason instead of a generic failure.
export const extractApiErrorMessage = (error, fallback) => {
   const data = error?.response?.data ?? error;
   const errors = data?.errors ?? data?.Errors;
   return (
      data?.message ||
      data?.Message ||
      (Array.isArray(errors) ? errors.filter(Boolean).join(" ") : "") ||
      (typeof data === "string" ? data : "") ||
      fallback
   );
};

// Opens an invoice/receipt URL in a new tab. No-ops on a falsy URL so callers
// can map over a list without pre-filtering.
export const openInvoice = (url) => {
   if (url) window.open(url, "_blank", "noopener,noreferrer");
};

// Maps a raw transaction record into the flat, display-ready row the
// PurchaseHistory table renders. `invoiceUrl` is preserved untouched for the
// download actions.
export const mapTransactionToRow = (transaction, index) => ({
   ...transaction,
   key: transaction.id ?? index,
   fileName: `Invoice_${formatDate(transaction.date)}.pdf`,
   date: formatDate(transaction.date),
   amountPaid: formatAmount(transaction.amountPaid, transaction.currency)
});

// Share of purchased funds that have been spent, clamped to [0, 100] for the
// usage bar. Returns 0 when nothing has been purchased (avoids divide-by-zero).
export const getUsagePercentage = (totalSpent, totalPurchased) =>
   totalPurchased ? Math.min((totalSpent / totalPurchased) * 100, 100) : 0;
